import { Actions, Async, FSpread } from '@client/new/helpers/types';
import {
  EditWorkoutModel,
  FormErrors,
  Hint,
  useEditWorkoutModel,
} from '../models/workoutEdit.model';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useEffect, useState } from 'react';
import ajax from '@client/components/ajax';
import {
  TagOptions,
  WorkoutItemTemplates,
  exercisesToTemplates,
  getCurrentBreadcrumb,
  getTemplateSelection,
  mapTagsToOptions,
} from './mappers';
import { useWorkoutClipboardStore } from '@client/new/stores/workoutClipboard.store';
import {
  BuilderType,
  Media,
  RunningExerciseType,
  Template,
  WorkoutItem,
} from '../models/workout.domain';
import _, { isEmpty } from 'lodash';
import {
  useGetCollectionsQuery,
  useGetLibraryContentQuery,
  useGetRunningFlagQuery,
} from 'generated/graphql';
import { navigate, PATHS } from '@client/routes';
import { mixTrack } from '@services/mixpanel';
import { exerciseTypeToString } from '../models/mappers';
import { LibraryVideo } from '../models/useVideos';
import { Folder, toFolder, toVideo, Video } from '../models/videos.domain';
import { ApolloError } from '@apollo/client';
import { ContentFilters } from '@client/new/common/library/helpers';

type PayloadMap = {
  save: never;
  publish: never;
  unpublish: never;
  archive: never;
  addTemplate: Template;
  uploadMedia: File;
  navigateToWorkouts: never;
  removeIntro: never;
  removeThumbnail: never;
  addToClipboard: WorkoutItem;
  removeWorkoutItem: number;
  addFromClipboard: WorkoutItem[];
  changeOrderInWorkout: { index: number; order: number };
  changeOrderInCircuit: { circuitIndex: number; index: number; order: number };
  addCircuitSet: { circuitIndex: number; index: number };
  addSet: number;
  removeSet: { exercise: number; set: number };
  removeCircuitSet: { circuitIndex: number; exercise: number; set: number };
  addMedia: { media: object; index: number; circuitIndex?: number };
  removeMedia: { index: number; isYoutube?: boolean; circuitIndex?: number };
  addWorkoutCircuitItem: number;
  removeCircuitItem: { circuitIndex: number; exerciseIndex: number };
  setTimeZone: string;
  setPublishedAt: string;
  clearSchedule: never;
  addRunCompleteAudio: { index: number; uuid: string; file: File };
  addSetAudio: {
    exerciseIndex: number;
    uuid: string;
    setIndex: number;
    file: File;
  };
  addOutdoorSet: number;
  addTreadmillSet: number;
  changeRunningVariant: { index: number; variant: RunningExerciseType };
  removeSetAudio: {
    exerciseIndex: number;
    setIndex: number;
  };
  removeRunCompleteAudio: number;
  exerciseTitleUpdate: {
    circuitIndex?: number;
    index: number;
    description: string;
    uuid: string;
  };
  applyHint: { circuitIndex?: number; index: number; hint: Hint; uuid: string };
  openClipboard: never;
  openLibrary: {
    index?: number;
    circuitIndex?: number;
    type?: 'short' | 'long';
    action: 'update' | 'add';
  };
  closeLibraryModal: never;
  addTemplatesFromVideos: string[];
  updateExerciseMediaFromSelection: string[];
  addVideo: object;
  openExerciseTypeInfo: never;
};
export type EditWorkoutVMAction = Actions<PayloadMap>;
export type EditWorkoutVMDispatcher = FSpread<PayloadMap>;

export type MenuItems = Array<{
  label: string;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
  activeInReadonly?: boolean;
}>;

export type EditWorkoutVM = {
  dispatch: EditWorkoutVMDispatcher;
  templateSelection: Array<{
    label: string;
    icon: IconProp;
    className: string;
    onClick: () => void;
  }>;
  influencerId: number;
  submitting: boolean;
  programs: Array<{ label: string; value: number }>;
  currentBreadcrumb: string;
  intro: {
    src: string | null;
    loading: boolean;
  };
  thumbnail: {
    src: string | null;
    loading: boolean;
  };
  exerciseTemplates: WorkoutItemTemplates;
  addFromClipboard: (values: WorkoutItem[]) => void;
  tagOptions: TagOptions;
  isClass: boolean;
  addTemplateFromLibrary: {
    isOpen: boolean;
    close: () => void;
    loading: boolean;
    library: {
      videos: Array<Video>;
      folders: Array<Folder>;
      error: ApolloError | undefined;
      loading: boolean;
      refreshLibrary: () => void;
    };
    type: ContentFilters | null;
    action: 'add' | 'update';
    addVideo: (value: object) => void;
    addSelection: (ids: string[]) => void;
  };
  isNewForm: boolean;
  publishAction: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
  readonly: boolean;
  workoutInfoId: string;
  errorMessage?: string;
  expandExercises: number[];
  singleExercisePublishModal: {
    isOpen: boolean;
    onClose: () => void;
    onContinue: () => void;
  };
  exerciseTypesInfoModal: {
    isOpen: boolean;
    onClose: () => void;
  };
} & Pick<EditWorkoutModel, 'watch' | 'control' | 'inputHints'>;

export const useEditWorkoutVM = ({
  influencerId,
  workoutId,
  builderType,
  locationQuery,
}: {
  influencerId: number;
  workoutId?: number;
  builderType: BuilderType;
  locationQuery: Record<string, string>;
}): Async<EditWorkoutVM> => {
  const { copy } = useWorkoutClipboardStore();
  const [programs, setPrograms] = useState<
    Array<{ label: string; value: number }>
  >([]);
  const [tagOptions, setTagOptions] = useState<TagOptions>([]);
  const [singlePublishModal, setSinglePublishModal] = useState({
    isOpen: false,
    confirmed: false,
  });
  const [exerciseTypeInfo, setExerciseTypeInfo] = useState(false);
  const [libraryState, setLibraryState] = useState<{
    isOpen: boolean;
    index?: number;
    circuitIndex: null | number;
    type: ContentFilters | null;
    action: 'add' | 'update';
  }>({
    isOpen: false,
    index: 0,
    circuitIndex: null,
    type: null,
    action: 'add',
  });

  const {
    data: libraryData,
    loading: libraryLoading,
    error: libraryError,
    refetch: refreshLibrary,
  } = useGetLibraryContentQuery({
    variables: {
      creatorId: influencerId,
    },
    fetchPolicy: 'network-only',
  });

  const asyncModel = useEditWorkoutModel({
    influencerId: influencerId,
    workoutId,
    builderType,
    locationQuery,
  });

  const { data: creatorData } = useGetRunningFlagQuery({
    fetchPolicy: 'cache-and-network',
    variables: { id: influencerId },
    skip: !influencerId,
  });

  const warnBeforeUnload = (ev: BeforeUnloadEvent) => {
    ev.preventDefault();
    ev.returnValue = '';
  };

  const { data: collectionsData } = useGetCollectionsQuery({
    variables: {
      influencerId,
      withDeleted: false,
    },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!collectionsData?.collections) return;

    window.addEventListener('beforeunload', warnBeforeUnload);

    setPrograms(
      collectionsData?.collections.map((collection) => ({
        label: collection.name,
        value: collection.id,
      })),
    );
    ajax.fetchAvailableWorkoutTags().then((data: any) => {
      const mappedData = Object.entries(
        _.groupBy(data, ({ category }) => category),
      );
      setTagOptions(mapTagsToOptions(mappedData));
    });

    return () => {
      window.removeEventListener('beforeunload', warnBeforeUnload);
    };
  }, [collectionsData?.collections]);

  if (asyncModel.type !== 'fetched') return asyncModel;
  const model = asyncModel.value;

  const dispatch: EditWorkoutVMDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as EditWorkoutVMAction;
    switch (action.type) {
      case 'save':
      case 'unpublish':
      case 'archive':
      case 'removeIntro':
      case 'removeThumbnail':
      case 'clearSchedule':
        model.dispatch(action.type);
        return null;
      case 'publish': {
        const showSingleExerciseInformation =
          builderType === 'workout' &&
          model.exercises.length === 1 &&
          !singlePublishModal.confirmed;
        if (showSingleExerciseInformation) {
          setSinglePublishModal((prev) => ({ ...prev, isOpen: true }));
          return null;
        }
        model.dispatch(action.type);
        return null;
      }
      case 'addTemplate':
        model.dispatch('addWorkoutItem', action.payload);
        return null;
      case 'uploadMedia':
        model.dispatch('uploadMedia', action.payload);
        return null;
      case 'navigateToWorkouts': {
        return navigate(PATHS.PARTNER_WORKOUTS(influencerId));
      }
      case 'addToClipboard':
        copy(action.payload);
        mixTrack({
          type: `Exercise Copied to Clipboard`,
          payload: {
            'Template Type': exerciseTypeToString(action.payload.type),
          },
        });
        return null;
      case 'removeWorkoutItem':
        model.dispatch('removeWorkoutItem', action.payload);
        return null;
      case 'addFromClipboard': {
        model.dispatch('addFromClipboard', action.payload);
        window.scrollTo(0, document.body.scrollHeight);
        mixTrack({
          type: `Exercise Pasted from Clipboard`,
          payload: {
            'Template Type':
              action.payload.length === 1
                ? exerciseTypeToString(action.payload[0].type)
                : 'Multiple',
          },
        });
        return null;
      }
      case 'changeOrderInWorkout':
      case 'changeOrderInCircuit':
      case 'addCircuitSet':
      case 'removeCircuitSet':
      case 'addSet':
      case 'removeSet':
      case 'addMedia':
      case 'removeMedia':
      case 'addWorkoutCircuitItem':
      case 'removeCircuitItem':
      case 'setTimeZone':
      case 'setPublishedAt':
      case 'addRunCompleteAudio':
      case 'addSetAudio':
      case 'addOutdoorSet':
      case 'addTreadmillSet':
      case 'changeRunningVariant':
      case 'removeRunCompleteAudio':
      case 'removeSetAudio':
      case 'exerciseTitleUpdate':
      case 'applyHint':
        model.dispatch(action.type, action.payload);
        return null;
      case 'openClipboard':
        mixTrack({
          type: 'Clipboard Tapped',
          payload: {},
        });
        return null;
      case 'openLibrary':
        setLibraryState({
          isOpen: true,
          index: action.payload.index,
          circuitIndex: action.payload?.circuitIndex ?? null,
          type: action.payload.type ?? null,
          action: action.payload.action,
        });
        return null;
      case 'closeLibraryModal':
        setLibraryState({
          isOpen: false,
          index: 0,
          circuitIndex: null,
          type: null,
          action: 'add',
        });
        return null;
      case 'addTemplatesFromVideos': {
        const selectedVideos = action.payload.map((id: string) =>
          (libraryData?.getCreatorsMedia || []).find(
            (item) => item.uuid === id,
          ),
        );
        model.dispatch('addWorkoutItemsWithVideoIds', {
          videos: selectedVideos as unknown as LibraryVideo[],
          circuitIndex: libraryState.circuitIndex,
        });
        setLibraryState({
          isOpen: false,
          index: 0,
          circuitIndex: null,
          type: null,
          action: 'add',
        });
        return null;
      }
      case 'updateExerciseMediaFromSelection': {
        const selectedVideos = action.payload.map((id: string) =>
          (libraryData?.getCreatorsMedia || []).find(
            (item) => item.uuid === id,
          ),
        );
        model.dispatch('addMedia', {
          media: selectedVideos[0] as Media,
          index: libraryState.index || 0,
          circuitIndex: libraryState.circuitIndex,
        });
        setLibraryState({
          isOpen: false,
          index: 0,
          circuitIndex: null,
          type: null,
          action: 'add',
        });
        return null;
      }
      case 'addVideo': {
        if (libraryState.action === 'update') {
          model.dispatch('addMedia', {
            media: action.payload,
            index: libraryState.index || 0,
            circuitIndex: libraryState.circuitIndex,
          });
        } else {
          model.dispatch('addTemplateWithMedia', {
            media: action.payload,
            circuitIndex: libraryState.circuitIndex,
          });
        }
        setLibraryState({
          isOpen: false,
          index: 0,
          circuitIndex: null,
          type: null,
          action: 'add',
        });
        return null;
      }
      case 'openExerciseTypeInfo':
        setExerciseTypeInfo(true);
        return null;
    }
  };

  const library = {
    videos: libraryData?.getCreatorsMedia
      ? libraryData?.getCreatorsMedia.map(toVideo)
      : [],
    folders: libraryData?.getCreatorsFolders
      ? libraryData?.getCreatorsFolders.map(toFolder)
      : [],
    loading: libraryLoading,
    error: libraryError,
    refreshLibrary: () => refreshLibrary(),
  };

  const showRunningTemplate =
    !!creatorData?.creator?.customizations?.isNewRunning;

  const errorMessage = getErrorMessage(asyncModel.value.formErrors);
  const expandExercises = asyncModel.value.formErrors.exercises
    ? Object.keys(asyncModel.value.formErrors.exercises)
    : [];

  return {
    type: 'fetched',
    value: {
      dispatch,
      addFromClipboard: (values: WorkoutItem[]) =>
        dispatch('addFromClipboard', values),
      control: model.control,
      templateSelection: getTemplateSelection(
        builderType,
        dispatch,
        showRunningTemplate,
      ),
      influencerId,
      submitting: model.submitting,
      programs,
      currentBreadcrumb: getCurrentBreadcrumb(builderType, workoutId),
      intro: model.intro,
      thumbnail: model.thumbnail,
      exerciseTemplates: exercisesToTemplates(
        model.exercises,
        dispatch,
        builderType,
      ),
      tagOptions,
      watch: model.watch,
      isClass: builderType == 'class',
      inputHints: model.inputHints,
      addTemplateFromLibrary: {
        isOpen: libraryState.isOpen,
        library,
        type: libraryState.type,
        action: libraryState.action,
        loading: libraryLoading,
        close: () => dispatch('closeLibraryModal'),
        addVideo: (value) => dispatch('addVideo', value),
        addSelection: (ids: string[]) =>
          libraryState.action === 'update'
            ? dispatch('updateExerciseMediaFromSelection', ids)
            : dispatch('addTemplatesFromVideos', ids),
      },
      isNewForm: !workoutId,
      publishAction: getPublishAction(model, dispatch),
      readonly: model.readonly,
      workoutInfoId: 'workoutInfoId',
      errorMessage,
      expandExercises: expandExercises.map((i) => +i),
      singleExercisePublishModal: {
        isOpen: singlePublishModal.isOpen,
        onClose: () =>
          setSinglePublishModal((prev) => ({ ...prev, isOpen: false })),
        onContinue: () => {
          setSinglePublishModal({ confirmed: true, isOpen: false });
          model.dispatch('publish');
        },
      },
      exerciseTypesInfoModal: {
        isOpen: exerciseTypeInfo,
        onClose: () => setExerciseTypeInfo(false),
      },
    },
  };
};

const getErrorMessage = (formErrors: FormErrors) => {
  if (isEmpty(formErrors)) return undefined;

  const onlyCollectionMissing =
    Object.keys(formErrors).length === 1 && formErrors?.programId;

  if (onlyCollectionMissing)
    return "Workout can't be published if it isn't assigned to a Collection";

  return 'Required fields are missing or invalid. Check the marked fields and try again.';
};

const getPublishAction = (
  model: EditWorkoutModel,
  dispatch: EditWorkoutVMDispatcher,
) => {
  if (!!model.workoutInfo.publishedAt && !model.workoutInfo.archivedAt) {
    return {
      label: 'Archive',
      onClick: () => dispatch('archive'),
      disabled: model.disableArchiveAction,
    };
  }
  return {
    label: 'Publish',
    onClick: () => dispatch('publish'),
  };
};
